import { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client/react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { useTranslation, Trans } from 'react-i18next';
import { mdiClose } from '@mdi/js';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Zod from 'zod';

import {
    ICreateAICourseInput,
    ICreateAICourseMutation,
    ICreateAICourseMutationVariables,
    CreateAICourseDocument,
} from 'graphql/types';
import { Button } from 'common/components/Button';
import { IconButton } from 'common/components/IconButton';
import {
    AILanguageSelectField,
    TextField,
    SelectField,
} from 'common/forms/Field';
import { AI_TRANSLATION_LANGUAGES } from 'common/constants/languages';
import { getNumbersOptions } from 'common/utils/form';
import { useApolloError } from 'common/hooks/useApolloError';
import { AIUsageIndicator } from 'common/components/AIUsageIndicator';
import { Divider } from 'common/components/Divider';
import { Link } from 'common/components/Link';

import { CourseAICreateTaskStatus } from './CourseAICreateTaskStatus';

export interface IProps {
    open: boolean;
    onClose?(): void;
}

interface IFormValues {
    language?: string;
    description: string;
    chapterCount: string;
    questionCount: string;
}

export const CourseAICreateModal = ({ open, onClose }: IProps) => {
    const [translate, i18n] = useTranslation();
    const { showApolloError } = useApolloError();
    const [watchingTaskId, setWatchingTaskId] = useState<string | undefined>();
    const [requestedChapterCount, setRequestedChapterCount] =
        useState<number>();
    // Default to true to prevent submission before usage data is loaded
    const [limitReached, setLimitReached] = useState(true);

    const [createAICourse, { loading: createLoading }] = useMutation<
        ICreateAICourseMutation,
        ICreateAICourseMutationVariables
    >(CreateAICourseDocument);

    const minMaxChapter = { min: 1, max: 10 };
    const minMaxQuestion = { min: 1, max: 4 };

    const validationSchema = Zod.object({
        language: Zod.string().optional(),
        description: Zod.string().min(1, translate('validate.required')),
        chapterCount: Zod.string().min(1, translate('validate.required')),
        questionCount: Zod.string().min(1, translate('validate.required')),
    });

    const defaultValues: IFormValues = useMemo(
        () => ({
            language: AI_TRANSLATION_LANGUAGES.find(
                (lang) => lang.translationKey === i18n.language.toLowerCase()
            )?.value,
            description: '',
            chapterCount: '2',
            questionCount: '2',
        }),
        [i18n.language]
    );

    const { control, handleSubmit, reset, formState } = useForm<IFormValues>({
        resolver: zodResolver(validationSchema),
        defaultValues,
    });

    const { isSubmitting } = formState;

    const handleCreateAICourse: SubmitHandler<IFormValues> = async (values) => {
        const course: ICreateAICourseInput = {
            topic: values.description,
            chapterCount: parseInt(values.chapterCount, 10),
            questionCount: parseInt(values.questionCount, 10),
            language: values.language,
        };

        try {
            const result = await createAICourse({ variables: { course } });

            const { taskId } = result.data?.createAICourse || {};

            if (taskId) {
                setWatchingTaskId(taskId);
                setRequestedChapterCount(parseInt(values.chapterCount, 10));
            }
        } catch (error) {
            showApolloError(error);
        }
    };

    useEffect(() => {
        if (open) {
            reset(defaultValues);
        } else {
            setWatchingTaskId(undefined);
            setRequestedChapterCount(undefined);
            setLimitReached(true);
        }
    }, [open, reset, defaultValues]);

    const numbersOptionsChapter = getNumbersOptions(minMaxChapter).map(
        (option) => ({
            ...option,
            value: option.value.toString(),
        })
    );
    const numbersOptionsQuestion = getNumbersOptions(minMaxQuestion).map(
        (option) => ({
            ...option,
            value: option.value.toString(),
        })
    );

    const isLoading = isSubmitting || createLoading;

    return (
        <Dialog fullWidth maxWidth={false} open={open} onClose={onClose}>
            {open && (
                <DialogContent
                    sx={{
                        height: '100vh',
                        width: '100%',
                        py: 10,
                    }}
                >
                    <Box sx={{ margin: 'auto', width: '100%' }}>
                        <Box sx={{ position: 'absolute', right: 10, top: 10 }}>
                            <IconButton
                                color="inherit"
                                iconPath={mdiClose}
                                iconSizeSm="2.8rem"
                                size="large"
                                onClick={onClose}
                            />
                        </Box>
                        <Container maxWidth="md">
                            {watchingTaskId && (
                                <CourseAICreateTaskStatus
                                    requestedChapterCount={
                                        requestedChapterCount
                                    }
                                    sx={{
                                        mt: 10,
                                        mx: 'auto',
                                        maxWidth: '400px',
                                    }}
                                    taskId={watchingTaskId}
                                    onRetry={() => setWatchingTaskId(undefined)}
                                />
                            )}

                            <Box
                                sx={{
                                    mt: 2,
                                    display: watchingTaskId ? 'none' : 'block',
                                }}
                            >
                                <Typography variant="h2">
                                    {translate('courseAICreateModal.title')}
                                </Typography>
                                <Typography sx={{ mt: 2 }} variant="body1">
                                    {translate('courseAICreateModal.text')}
                                </Typography>
                                <form
                                    onSubmit={handleSubmit(
                                        handleCreateAICourse
                                    )}
                                >
                                    <AILanguageSelectField
                                        control={control}
                                        name="language"
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        control={control}
                                        label={translate(
                                            'courseAICreateModal.descriptionFieldLabel'
                                        )}
                                        minRows={4}
                                        name="description"
                                    />

                                    <Grid container spacing={2}>
                                        <Grid size={{ sm: 6, xs: 12 }}>
                                            <SelectField
                                                fullWidth
                                                control={control}
                                                label={translate(
                                                    'courseAICreateModal.chaptersFieldLabel'
                                                )}
                                                name="chapterCount"
                                                options={numbersOptionsChapter}
                                            />
                                        </Grid>
                                        <Grid size={{ sm: 6, xs: 12 }}>
                                            <SelectField
                                                fullWidth
                                                control={control}
                                                label={translate(
                                                    'courseAICreateModal.questionsFieldLabel'
                                                )}
                                                name="questionCount"
                                                options={numbersOptionsQuestion}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography
                                        sx={{
                                            fontSize: '1.4rem',
                                            lineHeight: 1.6,
                                        }}
                                        variant="body2"
                                    >
                                        <Trans
                                            components={{
                                                a: (
                                                    <Link
                                                        rel="noreferrer"
                                                        underline="always"
                                                    />
                                                ),
                                            }}
                                            i18nKey="ai.disclaimer"
                                        />
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap-reverse',
                                            alignItems: 'center',
                                            columnGap: 2,
                                            rowGap: 3,
                                            mt: 2,
                                        }}
                                    >
                                        <Button
                                            color="primary"
                                            disabled={isLoading || limitReached}
                                            type="submit"
                                            variant="contained"
                                        >
                                            {translate(
                                                'courseAICreateModal.createButton'
                                            )}
                                        </Button>
                                        <Button
                                            disabled={isLoading}
                                            onClick={onClose}
                                        >
                                            {translate(
                                                'courseAICreateModal.cancelButton'
                                            )}
                                        </Button>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <AIUsageIndicator
                                            sx={{
                                                width: '100%',
                                                maxWidth: '300px',
                                            }}
                                            onLimitReached={setLimitReached}
                                        />
                                    </Box>
                                </form>
                            </Box>
                        </Container>
                    </Box>
                </DialogContent>
            )}
        </Dialog>
    );
};
